import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Calendar, MessageSquare, CheckSquare, Paperclip, AlertCircle } from 'lucide-react';
import { format, isPast, isToday } from 'date-fns';

export const KanbanCard = ({ 
  card, 
  columnId, 
  onClick, 
  isDragging: isDraggingProp,
  projectLabels 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: card.id,
    data: {
      type: 'card',
      card,
      columnId,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isOverdue = card.due_date && isPast(new Date(card.due_date)) && !isToday(new Date(card.due_date));
  const isDueToday = card.due_date && isToday(new Date(card.due_date));

  // Get checklist progress
  const getChecklistProgress = () => {
    if (!card.checklists || card.checklists.length === 0) return null;
    
    let total = 0;
    let completed = 0;
    card.checklists.forEach(checklist => {
      checklist.items?.forEach(item => {
        total++;
        if (item.completed) completed++;
      });
    });
    
    if (total === 0) return null;
    return { completed, total };
  };

  const checklistProgress = getChecklistProgress();

  const priorityColors = {
    low: 'bg-gray-500',
    medium: 'bg-blue-500',
    high: 'bg-orange-500',
    urgent: 'bg-red-500'
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className={`bg-[#1a1d21] rounded-lg p-3 cursor-pointer hover:bg-[#1a1d21]/80 border border-white/5 hover:border-white/10 transition-all group ${
        isDragging || isDraggingProp ? 'opacity-50 shadow-xl ring-2 ring-blue-500' : ''
      }`}
      data-testid={`kanban-card-${card.id}`}
    >
      {/* Cover Image */}
      {card.cover_image && (
        <div className="w-full h-32 rounded-md overflow-hidden mb-3 -mt-1 -mx-1">
          <img 
            src={card.cover_image} 
            alt="" 
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Labels */}
      {card.labels && card.labels.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {card.labels.map(label => (
            <span
              key={label.id}
              className="px-2 py-0.5 rounded-full text-[10px] font-medium text-white"
              style={{ backgroundColor: label.color }}
              title={label.name}
            >
              {label.name}
            </span>
          ))}
        </div>
      )}

      {/* Title */}
      <h4 className="text-white text-sm font-medium mb-2 line-clamp-2">{card.title}</h4>

      {/* Metadata Row */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Priority */}
        {card.priority && card.priority !== 'medium' && (
          <div className={`w-2 h-2 rounded-full ${priorityColors[card.priority]}`} title={`Priority: ${card.priority}`} />
        )}

        {/* Due Date */}
        {card.due_date && (
          <div 
            className={`flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${
              isOverdue ? 'bg-red-500/20 text-red-400' : 
              isDueToday ? 'bg-yellow-500/20 text-yellow-400' : 
              'bg-white/5 text-gray-400'
            }`}
          >
            {isOverdue && <AlertCircle size={12} />}
            <Calendar size={12} />
            <span>{format(new Date(card.due_date), 'MMM d')}</span>
          </div>
        )}

        {/* Checklist Progress */}
        {checklistProgress && (
          <div className={`flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${
            checklistProgress.completed === checklistProgress.total 
              ? 'bg-green-500/20 text-green-400' 
              : 'bg-white/5 text-gray-400'
          }`}>
            <CheckSquare size={12} />
            <span>{checklistProgress.completed}/{checklistProgress.total}</span>
          </div>
        )}

        {/* Comments Count */}
        {card.comments_count > 0 && (
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <MessageSquare size={12} />
            <span>{card.comments_count}</span>
          </div>
        )}

        {/* Attachments Count */}
        {card.attachments_count > 0 && (
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <Paperclip size={12} />
            <span>{card.attachments_count}</span>
          </div>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Assignees */}
        {card.assignees && card.assignees.length > 0 && (
          <div className="flex -space-x-1">
            {card.assignees.slice(0, 3).map(assignee => (
              <div
                key={assignee.id}
                className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-medium border border-[#1a1d21]"
                style={{ backgroundColor: assignee.avatar_color }}
                title={assignee.name}
              >
                {assignee.name?.charAt(0).toUpperCase()}
              </div>
            ))}
            {card.assignees.length > 3 && (
              <div className="w-6 h-6 rounded-full bg-gray-600 flex items-center justify-center text-white text-[10px] border border-[#1a1d21]">
                +{card.assignees.length - 3}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
